// Appointment-grant and delegation controls for the Organisation page.
// These are UX only: backend/routers/organisation.py validates every grant,
// scope and anchor and remains authoritative. organisationAuthorityControls
// .test.js fails if DELEGABLE_CAPABILITIES drifts from the backend set.
import { REPORTING_CAPABILITY_CONTROLS } from "./reportingUtils";
import { ATTENDANCE_CAPABILITY_CONTROLS } from "./staffAttendanceUtils";
import { FINANCE_ADMIN_CAPABILITY_CONTROLS, FINANCE_CAPABILITY_CONTROLS } from "./financeCapabilityControls";

export const ACADEMIC_CAPABILITY_CONTROLS = [
  ["student.view", "View students", "Read student records within the selected scope."],
  ["attendance.view", "View student attendance", "Read student attendance within the selected scope."],
  ["attendance.mark", "Mark student attendance", "Record student attendance within the selected scope."],
  ["assessment.view", "View assessments", "Read assessment results within the selected scope."],
  ["assessment.review", "Review assessment results", "Review submitted assessment results within the selected scope."],
  ["assessment.publish", "Publish assessment results", "Publish reviewed assessment results within the selected scope."],
  ["assessment.unlock", "Unlock published assessments", "Reopen published assessment results within the selected scope."],
];

// Capabilities the backend accepts on a delegation (DELEGABLE_CAPABILITIES).
export const DELEGABLE_CAPABILITIES = [
  "student.view", "attendance.view", "attendance.mark", "assessment.review", "assessment.publish", "assessment.unlock",
  "report.view_assigned", "report.action", "report.escalate", "report.confidential_route", "report.confidential_direct",
];

// Access administration (access.grant_finance) is deliberately not offered
// as an appointment grant; the backend reserves it for super admins.
export const ASSIGNMENT_GRANT_SECTIONS = [
  ["Academic", ACADEMIC_CAPABILITY_CONTROLS], ["Reporting", REPORTING_CAPABILITY_CONTROLS],
  ["Staff Attendance", ATTENDANCE_CAPABILITY_CONTROLS], ["Finance", FINANCE_CAPABILITY_CONTROLS],
];

const ALL_CONTROLS = [...ACADEMIC_CAPABILITY_CONTROLS, ...REPORTING_CAPABILITY_CONTROLS, ...ATTENDANCE_CAPABILITY_CONTROLS, ...FINANCE_CAPABILITY_CONTROLS, ...FINANCE_ADMIN_CAPABILITY_CONTROLS];
const LABELS = Object.fromEntries(ALL_CONTROLS.map(([code, label]) => [code, label]));
export const capabilityLabel = (code) => LABELS[code] || code;

export const DELEGATION_CAPABILITY_CONTROLS = DELEGABLE_CAPABILITIES.map((code) => ALL_CONTROLS.find(([item]) => item === code) || [code, code]);

export const SCOPE_LABELS = {
  self: "Own records only", assigned_class: "Classes this person teaches", assigned_students: "Students in classes this person teaches",
  assigned_unit: "Organisational unit and its sub-units", selected_classes: "Selected class(es)", selected_programmes: "Selected programme(s)",
  academic_all: "All academic placements", operations_all: "All operations", finance_all: "Finance-wide (every student)",
  institution_all: "Entire institution", executive: "Entire institution (executive)",
};
export const scopeLabel = (scope) => SCOPE_LABELS[scope] || scope || "Not set";
export const ASSIGNMENT_SCOPES = ["self", "assigned_class", "assigned_students", "assigned_unit", "selected_classes", "selected_programmes", "academic_all", "operations_all", "finance_all", "institution_all", "executive"];
export const DELEGATION_SCOPES = ["self", "assigned_class", "assigned_students", "assigned_unit", "selected_classes", "selected_programmes", "academic_all", "operations_all", "finance_all"];

export const SOURCE_LABELS = { POSITION: "Position default", ASSIGNMENT: "Appointment grant", DELEGATION: "Temporary delegation", DIRECT_OVERRIDE: "Direct override (exceptional)", LEGACY_ROLE: "Legacy role" };

// Only the anchor a scope needs is sent; switching scope drops the others.
export const withScopeAnchors = (record) => ({
  ...record,
  class_ids: record.scope === "selected_classes" ? record.class_ids || [] : [],
  program_ids: record.scope === "selected_programmes" ? record.program_ids || [] : [],
});

// A friendly pre-check mirroring the backend's fail-closed anchor rules.
export const scopeAnchorProblem = (record, { unitRequired = false } = {}) => {
  if (unitRequired && record.scope === "assigned_unit" && !record.unit_id) return "Select the unit this scope is anchored to.";
  if (record.scope === "selected_classes" && !(record.class_ids || []).length) return "Select at least one class for this scope.";
  if (record.scope === "selected_programmes" && !(record.program_ids || []).length) return "Select at least one programme for this scope.";
  return "";
};
